"use client";

import { Box, Flex } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineXMark,
} from "react-icons/hi2";
import { Text } from "@/components";
import { useAuth } from "@/lib/contexts";
import { dashboardStickyBelowTopBar } from "@/lib/layout/dashboard-sticky-top";
import { useThemeTokens } from "@/lib/theme/tokens";
import {
  dismissProductNotice,
  readDismissedNoticeIds,
} from "./dismiss-product-notice";
import {
  activeProductNotices,
  PRODUCT_NOTICES,
  type ProductNotice,
  productNoticeShell,
} from "./product-notices";

function noticeUserId(userId: number | string | null | undefined): string {
  if (userId == null || userId === "") return "0";
  return String(userId);
}

export function ProductNoticeBanner() {
  const pathname = usePathname();
  const user = useAuth();
  const theme = useThemeTokens();
  const userId = noticeUserId(user?.id);
  const shell = productNoticeShell(pathname);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setDismissedIds(readDismissedNoticeIds(userId, localStorage));
    setReady(true);
  }, [userId]);

  if (!ready || !shell) return null;

  const notices = activeProductNotices(
    PRODUCT_NOTICES,
    shell,
    new Date(),
    dismissedIds,
  );
  if (notices.length === 0) return null;

  const close = (notice: ProductNotice) => {
    setDismissedIds(dismissProductNotice(userId, notice.id, localStorage));
  };
  const isDark = theme.mode === "dark";

  return (
    <Flex
      direction="column"
      gap={2}
      px={{ base: 4, lg: 8 }}
      mb={4}
      position="sticky"
      top={dashboardStickyBelowTopBar(0)}
      zIndex={19}
      data-testid="product-notice-banner"
    >
      {notices.map((notice) => {
        const attention = notice.tone === "attention";
        return (
          <Flex
            key={notice.id}
            align="flex-start"
            gap={3}
            px={4}
            py={3}
            borderRadius="12px"
            borderWidth="1px"
            borderColor={
              attention
                ? isDark
                  ? "orange.300"
                  : "orange.400"
                : isDark
                  ? "green.300"
                  : "green.400"
            }
            bg={
              attention
                ? isDark
                  ? "#9A3412"
                  : "#FFEDD5"
                : isDark
                  ? "#166534"
                  : "#DCFCE7"
            }
            data-testid="product-notice"
            data-notice-id={notice.id}
          >
            <Box
              color={
                attention
                  ? isDark
                    ? "orange.200"
                    : "orange.700"
                  : isDark
                    ? "green.200"
                    : "green.700"
              }
              flexShrink={0}
              mt="1px"
              aria-hidden
            >
              {attention ? (
                <HiOutlineExclamationTriangle size={18} />
              ) : (
                <HiOutlineCheckCircle size={18} />
              )}
            </Box>
            <Text
              flex="1"
              minW={0}
              fontSize="sm"
              fontWeight="600"
              lineHeight="1.5"
              color={theme.text.primary}
            >
              {notice.message}
            </Text>
            <Box
              as="button"
              type="button"
              aria-label="Dismiss announcement"
              onClick={() => close(notice)}
              color={theme.text.secondary}
              flexShrink={0}
              lineHeight={1}
              mt="1px"
            >
              <HiOutlineXMark size={18} />
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}
